import {Checkbox, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {Controller} from 'react-hook-form';

import type {ReactNode} from 'react';
import type {Control, FieldValues, Path} from 'react-hook-form';

export interface DeclarationConsentRowProps<TValues extends FieldValues> {
  name: Path<TValues>;
  control: Control<TValues>;
  /** Unique id for the checkbox input — used for the htmlFor/id pair. */
  id: string;
  /** Already-translated label content — may be a string or a <Trans> node. */
  children: ReactNode;
  /** When true, adds a top border to separate this row from the one above it. */
  hasDivider?: boolean;
  disabled?: boolean;
}

export function DeclarationConsentRow<TValues extends FieldValues>({
  name,
  control,
  id,
  children,
  hasDivider = false,
  disabled = false
}: DeclarationConsentRowProps<TValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({field}) => (
        <label
          htmlFor={id}
          className={cn(
            'flex items-start gap-3 px-4 py-3 transition-colors',
            disabled ? 'cursor-default' : 'cursor-pointer',
            hasDivider && 'border-t border-border'
          )}
        >
          <Checkbox
            id={id}
            checked={field.value as boolean}
            onCheckedChange={checked => field.onChange(checked === true)}
            disabled={disabled}
            className="mt-0.5 shrink-0"
          />
          <Typography size="sm" className="leading-relaxed whitespace-normal">
            {children}
          </Typography>
        </label>
      )}
    />
  );
}
